<template>
  <div class="benefits-confirm-modal-root">
    <div v-if="value" class="benefits-confirm-modal-backdrop" @mousedown.self="cancel">
      <KvCard class="benefits-confirm-modal" content-class="benefits-confirm-modal-content">
        <template #text>
          <button class="benefits-confirm-modal-close" type="button" :aria-label="cancelLabel" @click="cancel">
            <Icon icon-class="ui-x-close" size="5" />
          </button>

          <h4 class="benefits-confirm-modal-title">{{ title }}</h4>
          <p v-if="description" class="benefits-confirm-modal-desc">{{ description }}</p>

          <slot></slot>

          <div class="benefits-confirm-modal-actions">
            <KvButton class="benefits-confirm-modal-cancel" type="button" variant="outlined" @click="cancel">
              {{ cancelLabel }}
            </KvButton>

            <KvButton
              class="benefits-confirm-modal-confirm"
              :class="`is-${variant}`"
              type="button"
              :prepend-icon="confirmIcon"
              :disabled="confirmDisabled"
              :loading="loading"
              @click="$emit('confirm')"
            >
              {{ confirmLabel }}
            </KvButton>
          </div>
        </template>
      </KvCard>
    </div>
  </div>
</template>

<script>
import KvCard from "../../components/ui/kv-card.vue";
import KvButton from "../../components/form/kv-button.vue";
import Icon from "../../components/icon/icon.vue";

export default {
  name: "BenefitsConfirmModal",
  components: {
    KvCard,
    KvButton,
    Icon,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    description: { type: String, default: "" },
    cancelLabel: { type: String, required: true },
    confirmLabel: { type: String, required: true },
    confirmIcon: { type: String, default: "" },
    variant: { type: String, default: "danger" },
    confirmDisabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    cancel() {
      if (this.loading) return;
      this.$emit("input", false);
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-confirm-modal-backdrop {
  align-items: center;
  background: rgba(17, 25, 39, 0.4);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;
}

::v-deep .benefits-confirm-modal {
  background: #fcfcfd !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  max-height: calc(100vh - 32px);
  max-width: 520px !important;
  overflow-y: auto;
  width: 92% !important;
}

::v-deep .benefits-confirm-modal-content {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px !important;
  position: relative;
  text-align: center;
  width: 100%;
}

.benefits-confirm-modal-close {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #9da4ae;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #4d5761;
  }
}

.benefits-confirm-modal-title {
  color: #4d5761;
  font-family: "Inter", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
  width: 100%;
}

.benefits-confirm-modal-desc {
  color: #4d5761;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  width: 100%;
}

.benefits-confirm-modal-actions {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
}

.benefits-confirm-modal-cancel {
  color: #4d5761 !important;
  height: 44px !important;
  min-width: 120px !important;
}

.benefits-confirm-modal-confirm {
  height: 44px !important;
  min-width: 140px !important;

  &.is-danger {
    background-color: #fee4e2 !important;
    border-color: #fee4e2 !important;
    color: #f04438 !important;

    &:hover:not(:disabled) {
      background-color: #fcd5d3 !important;
    }
  }

  &.is-primary {
    background-color: #2aaa4c !important;
    border-color: #2aaa4c !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: #f3f4f6 !important;
    border-color: #f3f4f6 !important;
    color: #9da4ae !important;
    cursor: not-allowed;
  }
}
</style>
